import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle } from "lucide-react";

export function useValidationPopup() {
  const [message, setMessage] = useState(null);
  const notify = useCallback((m) => setMessage(m), []);
  const close = useCallback(() => setMessage(null), []);
  const popup = <ValidationPopup message={message} onClose={close} />;
  return { notify, popup };
}

function ValidationPopup({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [message, onClose]);

  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] grid place-items-center bg-navy-deep/70 p-5 backdrop-blur-sm"
          data-testid="validation-popup"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full modal-w-sm rounded-3xl border border-gold/30 bg-card p-8 text-center shadow-[0_30px_80px_-20px_rgba(13,29,71,0.7)]"
            role="alertdialog"
            aria-modal="true"
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.15, 1] }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gold/15"
            >
              <AlertCircle className="h-7 w-7 text-gold" />
            </motion.span>
            <h2 className="mt-5 font-heading text-xl font-bold tracking-tight text-foreground">
              Just one quick thing
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>
            <button
              type="button"
              onClick={onClose}
              data-testid="validation-popup-dismiss"
              className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-gold px-7 py-3 text-sm font-semibold text-navy-deep transition-transform hover:-translate-y-0.5"
            >
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
